import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  booleanAttribute,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import { LUCIDE_ICONS } from '@compsych-ui-components/shared/assets/lucide-icons.generated';
import { ButtonComponent, ButtonVariant } from '../button/button';

/** Size variant of a `compsych-text-input`. Controls input height, padding, and font size. */
export type TextInputSize = 'small' | 'medium' | 'large';

/** Native input type. */
export type TextInputType = 'text' | 'password';

/** Configuration for the optional button rendered at the right of the label row. */
export interface TextInputLabelButton {
  /** Stable identifier emitted by `labelButtonClick` when clicked. */
  id: string;
  /** Text displayed on the button. */
  label: string;
  /** Visual style of the button. Defaults to `'text'`. */
  variant?: ButtonVariant;
  /** Lucide icon name shown before the label. */
  leadingIcon?: string;
  /** Lucide icon name shown after the label. */
  trailingIcon?: string;
  /** Hides the label and renders a square icon-only button. Requires `leadingIcon`. */
  iconOnly?: boolean;
}

let nextId = 0;

/**
 * A labelled text input that wraps a native `<input>` element.
 * Implements `ControlValueAccessor` so it works with both reactive forms
 * (`formControlName`, `[formControl]`) and template-driven forms (`ngModel`).
 * The error visual state is driven automatically by the bound `FormControl`'s
 * validity once the field has been touched, and can also be set explicitly via
 * the `error` input for use outside of Angular forms.
 */
@Component({
  selector: 'compsych-text-input',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent],
  template: `
    <div [class]="wrapperClasses">
      <div class="label-row" [class.sr-only]="hideLabel">
        <label class="label" [for]="inputId">
          {{ label }}
          @if (isRequired) {
            <span class="required-marker" aria-hidden="true"> *</span>
          }
        </label>
        @if (parsedLabelButton) {
          <compsych-button
            [label]="parsedLabelButton.label"
            [variant]="parsedLabelButton.variant ?? 'text'"
            size="small"
            [leadingIcon]="parsedLabelButton.leadingIcon"
            [trailingIcon]="parsedLabelButton.trailingIcon"
            [iconOnly]="!!parsedLabelButton.iconOnly"
            [disabled]="disabled"
            (click)="onLabelButtonClick()"
          />
        }
      </div>
      <div class="input-wrapper">
        @if (leadingIconSvg) {
          <span class="input-icon input-icon--leading" aria-hidden="true" [innerHTML]="leadingIconSvg"></span>
        }
        <input
          class="input"
          [class.has-leading-icon]="leadingIcon"
          [class.has-trailing-icon]="trailingIcon"
          [id]="inputId"
          [name]="name || inputId"
          [type]="type"
          [placeholder]="placeholder"
          [value]="value"
          [disabled]="disabled"
          [readOnly]="readonly"
          [attr.required]="isRequired ? '' : null"
          [class.error]="hasError"
          [attr.aria-invalid]="hasError ? 'true' : null"
          [attr.aria-describedby]="describedBy"
          (input)="onInput($event)"
          (blur)="onBlur()"
        />
        @if (trailingIconSvg) {
          @if (trailingIconClick.observed) {
            <button class="input-icon input-icon--trailing input-icon--btn" type="button" [disabled]="disabled" [attr.aria-label]="trailingIconAriaLabel || null" [innerHTML]="trailingIconSvg" (click)="trailingIconClick.emit()"></button>
          } @else {
            <span class="input-icon input-icon--trailing" aria-hidden="true" [innerHTML]="trailingIconSvg"></span>
          }
        }
      </div>
      @if (displayedSupportingText) {
        <span class="supporting-text" [class.supporting-text--error]="hasError" [id]="inputId + '-supporting'">
          {{ displayedSupportingText }}
        </span>
      }
    </div>
  `,
  styleUrl: './text-input.css',
})
export class TextInputComponent implements ControlValueAccessor, OnChanges {
  private readonly _autoId = `cmp-text-input-${++nextId}`;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);
  private _onChange: (value: string) => void = () => {};
  private _onTouched: () => void = () => {};

  /** Injected when the component is used inside a reactive or template-driven form. */
  protected readonly ngControl: NgControl | null;

  constructor() {
    const ctrl = inject(NgControl, { optional: true, self: true });
    if (ctrl) ctrl.valueAccessor = this;
    this.ngControl = ctrl;
  }

  /** Label displayed above the input. */
  @Input() label = '';
  /** Placeholder text shown inside the input when empty. */
  @Input() placeholder = '';
  /** Current value of the input. When used in a form, the form controls the value. */
  @Input() value = '';
  /** Native input type. Defaults to `'text'`. Use `'password'` to mask the value. */
  @Input() type: TextInputType = 'text';
  /** Size variant — controls height, padding, and font size. Defaults to `'medium'`. */
  @Input() size: TextInputSize = 'medium';
  /** Native `name` attribute forwarded to the `<input>`. Defaults to the auto-generated id. */
  @Input() name?: string;
  /** Native `id` attribute for the `<input>`. Auto-generated if omitted. */
  @Input() id?: string;
  /**
   * Id(s) of external elements that describe the input, space-separated.
   * Forwarded to `aria-describedby` on the native `<input>`.
   */
  @Input() ariaDescribedBy?: string;
  /** Text displayed below the input. Shown in red when the input is in error state. */
  @Input() supportingText = '';
  /**
   * Static error text shown in red when the input is in error state.
   * Used when the input is outside a form (with `[error]="true"`).
   * When inside a form, prefer `errorMessages` for per-validator messages.
   */
  @Input() errorMessage = '';
  /**
   * Map of Angular validator error keys to display strings.
   * The component reads `ngControl.errors` to determine which validator failed
   * and shows the matching message in red below the input.
   * Example: `{ required: 'Username is required', minlength: 'Min 8 characters' }`
   */
  @Input() errorMessages: Record<string, string> = {};

  private _labelButton: TextInputLabelButton | null = null;

  /**
   * JSON string configuring the optional button at the right of the label row.
   * Hidden when empty or omitted. Example: `'{"id":"show","label":"Show"}'`
   * Clicking emits the button's `id` via the `labelButtonClick` output.
   */
  @Input() set labelButton(json: string) {
    if (!json) { this._labelButton = null; return; }
    try {
      this._labelButton = JSON.parse(json) as TextInputLabelButton;
    } catch {
      this._labelButton = null;
    }
  }

  /** @internal Template access to the parsed label button config. */
  protected get parsedLabelButton(): TextInputLabelButton | null {
    return this._labelButton;
  }

  /**
   * Adds a blue asterisk after the label. When used in a form, this is set
   * automatically if the bound `FormControl` has `Validators.required`.
   * Can also be set explicitly for non-form use. Supports boolean coercion.
   */
  @Input({ transform: booleanAttribute }) required = false;
  /**
   * Applies the error visual state (red border) explicitly. When used in a form,
   * the error state is driven automatically from the `FormControl`'s validity once
   * the field is touched — this input is only needed outside of forms.
   * Supports boolean coercion.
   */
  @Input({ transform: booleanAttribute }) error = false;
  /** Disables the input. When used in a form, also responds to `FormControl.disable()`. Supports boolean coercion. */
  @Input({ transform: booleanAttribute }) disabled = false;
  /** Makes the input read-only — focusable and copyable but not editable. Value is still submitted with the form. Supports boolean coercion. */
  @Input({ transform: booleanAttribute }) readonly = false;
  /** Visually hides the label while keeping it accessible to screen readers. Supports boolean coercion. */
  @Input({ transform: booleanAttribute }) hideLabel = false;
  /** Lucide icon name rendered inside the input before the text (e.g. `'search'`). */
  @Input() leadingIcon?: string;
  /** Lucide icon name rendered inside the input after the text (e.g. `'eye'`). */
  @Input() trailingIcon?: string;
  /** Accessible label for the trailing icon button. Required when `trailingIconClick` is used. */
  @Input() trailingIconAriaLabel = '';

  protected leadingIconSvg: SafeHtml | null = null;
  protected trailingIconSvg: SafeHtml | null = null;

  ngOnChanges(): void {
    this.leadingIconSvg = this.leadingIcon ? this.buildIconSvg(this.leadingIcon) : null;
    this.trailingIconSvg = this.trailingIcon ? this.buildIconSvg(this.trailingIcon) : null;
  }

  private buildIconSvg(name: string): SafeHtml {
    const svg = (LUCIDE_ICONS[name] ?? '').replace('<svg ', '<svg width="20" height="20" ');
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  }

  /** Emits the new string value whenever the user types. For use outside Angular forms; inside forms use the `FormControl` value stream. */
  @Output() readonly valueChange = new EventEmitter<string>();
  /** Emits the label button's `id` when it is clicked. */
  @Output() readonly labelButtonClick = new EventEmitter<string>();
  /** Emitted when the trailing icon button is clicked. When observed, the trailing icon renders as an interactive button. */
  @Output() readonly trailingIconClick = new EventEmitter<void>();

  get inputId(): string {
    return this.id ?? this._autoId;
  }

  /** True when the bound FormControl is invalid and touched, or when `error` is explicitly set. */
  get hasError(): boolean {
    return this.error || !!(this.ngControl?.invalid && this.ngControl.touched);
  }

  /** True when `required` is set explicitly, or when the bound FormControl has Validators.required. */
  get isRequired(): boolean {
    return this.required || !!(this.ngControl?.control?.hasValidator(Validators.required));
  }

  private get activeErrorMessage(): string {
    const errors = this.ngControl?.errors;
    if (!errors) return '';
    for (const key of Object.keys(errors)) {
      if (this.errorMessages[key]) return this.errorMessages[key];
    }
    return '';
  }

  get displayedSupportingText(): string {
    if (this.hasError) {
      return this.activeErrorMessage || this.errorMessage || this.supportingText;
    }
    return this.supportingText;
  }

  get describedBy(): string | null {
    const parts: string[] = [];
    if (this.ariaDescribedBy) parts.push(this.ariaDescribedBy);
    if (this.supportingText || this.errorMessage || Object.keys(this.errorMessages).length) {
      parts.push(this.inputId + '-supporting');
    }
    return parts.length ? parts.join(' ') : null;
  }

  get wrapperClasses(): string {
    return ['cmp-text-field', this.size, this.disabled ? 'disabled' : '']
      .filter(Boolean)
      .join(' ');
  }

  // ── ControlValueAccessor ────────────────────────────────────────────────────

  writeValue(value: string | null): void {
    this.value = value ?? '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  /** Called by Angular when the bound FormControl is programmatically disabled or enabled. */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  // ── Event handlers ──────────────────────────────────────────────────────────

  protected onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.value = value;
    this._onChange(value);
    this.valueChange.emit(value);
  }

  protected onBlur(): void {
    this._onTouched();
    this.cdr.markForCheck();
  }

  protected onLabelButtonClick(): void {
    if (this._labelButton) {
      this.labelButtonClick.emit(this._labelButton.id);
    }
  }
}
